/**
 * Shared polyhedron mesh builder (no Three.js) — API tessellate + UI BufferGeometry.
 * Industry path: Platonic / icosphere base OR seeded sphere samples → convex
 * hull, then optional radial irregularity on welded verts (merge-equivalent).
 */

export type PolyhedronFamily =
	| "tetrahedron"
	| "octahedron"
	| "icosahedron"
	| "dodecahedron"
	| "convex";

export interface PolyhedronMeshVertex {
	x: number;
	y: number;
	z: number;
}

/** Triangle as three vertex indices into `vertices`. */
export type PolyhedronMeshTriangle = readonly [number, number, number];

export interface PolyhedronMesh {
	vertices: PolyhedronMeshVertex[];
	triangles: PolyhedronMeshTriangle[];
	family: PolyhedronFamily;
	faceCount: number;
}

export interface BuildPolyhedronMeshParams {
	family?: PolyhedronFamily;
	/** Platonic / icosphere subdivision depth (0 = true low-poly). */
	detail?: number;
	/**
	 * Configurable face budget for `convex` family (also used when family
	 * omitted and targetFaces ≥ 4). Approx — hull face count tracks N.
	 */
	targetFaces?: number;
	/** 0 = clean; 1 = strong radial jitter (seeded). */
	irregularity?: number;
	seed?: number;
}

function clamp(n: number, lo: number, hi: number): number {
	return Math.min(hi, Math.max(lo, n));
}

/** Deterministic [0,1). */
export function polyhedronHash01(seed: number, i: number): number {
	const n = Math.sin(seed * 12.9898 + i * 78.233) * 43758.5453;
	return n - Math.floor(n);
}

function normalize(v: PolyhedronMeshVertex): PolyhedronMeshVertex {
	const len = Math.hypot(v.x, v.y, v.z) || 1;
	return { x: v.x / len, y: v.y / len, z: v.z / len };
}

function mid(
	a: PolyhedronMeshVertex,
	b: PolyhedronMeshVertex,
): PolyhedronMeshVertex {
	return normalize({
		x: (a.x + b.x) * 0.5,
		y: (a.y + b.y) * 0.5,
		z: (a.z + b.z) * 0.5,
	});
}

function subdivide(
	vertices: PolyhedronMeshVertex[],
	triangles: PolyhedronMeshTriangle[],
	detail: number,
): { vertices: PolyhedronMeshVertex[]; triangles: PolyhedronMeshTriangle[] } {
	const verts = [...vertices];
	let tris = [...triangles];
	const depth = clamp(Math.floor(detail), 0, 3);
	for (let d = 0; d < depth; d++) {
		const next: PolyhedronMeshTriangle[] = [];
		const edgeCache = new Map<string, number>();
		const edgeMid = (i: number, j: number): number => {
			const key = i < j ? `${i}_${j}` : `${j}_${i}`;
			const hit = edgeCache.get(key);
			if (hit !== undefined) {
				return hit;
			}
			const m = mid(verts[i], verts[j]);
			const idx = verts.length;
			verts.push(m);
			edgeCache.set(key, idx);
			return idx;
		};
		for (const [a, b, c] of tris) {
			const ab = edgeMid(a, b);
			const bc = edgeMid(b, c);
			const ca = edgeMid(c, a);
			next.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca]);
		}
		tris = next;
	}
	return { vertices: verts, triangles: tris };
}

/** Regular tetrahedron on unit sphere. */
function tetrahedronBase(): {
	vertices: PolyhedronMeshVertex[];
	triangles: PolyhedronMeshTriangle[];
} {
	const v = [
		normalize({ x: 1, y: 1, z: 1 }),
		normalize({ x: 1, y: -1, z: -1 }),
		normalize({ x: -1, y: 1, z: -1 }),
		normalize({ x: -1, y: -1, z: 1 }),
	];
	return {
		vertices: v,
		triangles: [
			[0, 1, 2],
			[0, 3, 1],
			[0, 2, 3],
			[1, 3, 2],
		],
	};
}

function octahedronBase(): {
	vertices: PolyhedronMeshVertex[];
	triangles: PolyhedronMeshTriangle[];
} {
	const v: PolyhedronMeshVertex[] = [
		{ x: 1, y: 0, z: 0 },
		{ x: -1, y: 0, z: 0 },
		{ x: 0, y: 1, z: 0 },
		{ x: 0, y: -1, z: 0 },
		{ x: 0, y: 0, z: 1 },
		{ x: 0, y: 0, z: -1 },
	];
	return {
		vertices: v,
		triangles: [
			[0, 2, 4],
			[0, 4, 3],
			[0, 3, 5],
			[0, 5, 2],
			[1, 4, 2],
			[1, 3, 4],
			[1, 5, 3],
			[1, 2, 5],
		],
	};
}

function icosahedronBase(): {
	vertices: PolyhedronMeshVertex[];
	triangles: PolyhedronMeshTriangle[];
} {
	const t = (1 + Math.sqrt(5)) / 2;
	const raw: PolyhedronMeshVertex[] = [
		{ x: -1, y: t, z: 0 },
		{ x: 1, y: t, z: 0 },
		{ x: -1, y: -t, z: 0 },
		{ x: 1, y: -t, z: 0 },
		{ x: 0, y: -1, z: t },
		{ x: 0, y: 1, z: t },
		{ x: 0, y: -1, z: -t },
		{ x: 0, y: 1, z: -t },
		{ x: t, y: 0, z: -1 },
		{ x: t, y: 0, z: 1 },
		{ x: -t, y: 0, z: -1 },
		{ x: -t, y: 0, z: 1 },
	].map(normalize);
	return {
		vertices: raw,
		triangles: [
			[0, 11, 5],
			[0, 5, 1],
			[0, 1, 7],
			[0, 7, 10],
			[0, 10, 11],
			[1, 5, 9],
			[5, 11, 4],
			[11, 10, 2],
			[10, 7, 6],
			[7, 1, 8],
			[3, 9, 4],
			[3, 4, 2],
			[3, 2, 6],
			[3, 6, 8],
			[3, 8, 9],
			[4, 9, 5],
			[2, 4, 11],
			[6, 2, 10],
			[8, 6, 7],
			[9, 8, 1],
		],
	};
}

/**
 * Regular dodecahedron verts (cube ± golden-ratio) → convex hull tris.
 * Matches Three.DodecahedronGeometry(detail=0) face count family.
 */
function dodecahedronBase(): {
	vertices: PolyhedronMeshVertex[];
	triangles: PolyhedronMeshTriangle[];
} {
	const phi = (1 + Math.sqrt(5)) / 2;
	const inv = 1 / phi;
	const raw: PolyhedronMeshVertex[] = [];
	for (const sx of [-1, 1]) {
		for (const sy of [-1, 1]) {
			for (const sz of [-1, 1]) {
				raw.push({ x: sx, y: sy, z: sz });
			}
		}
	}
	for (const s of [-1, 1]) {
		for (const t of [-1, 1]) {
			raw.push({ x: 0, y: s * inv, z: t * phi });
			raw.push({ x: s * inv, y: t * phi, z: 0 });
			raw.push({ x: s * phi, y: 0, z: t * inv });
		}
	}
	const vertices = raw.map(normalize);
	return { vertices, triangles: convexHullTriangles(vertices) };
}

/** Fibonacci sphere samples. */
function fibonacciSphere(count: number, seed: number): PolyhedronMeshVertex[] {
	const n = Math.max(4, count);
	const out: PolyhedronMeshVertex[] = [];
	const offset = 2 / n;
	const rnd = polyhedronHash01(seed, 99) * Math.PI * 2;
	for (let i = 0; i < n; i++) {
		const y = i * offset - 1 + offset / 2;
		const r = Math.sqrt(Math.max(0, 1 - y * y));
		const phi = i * 2.399963229728653 + rnd;
		out.push({ x: Math.cos(phi) * r, y, z: Math.sin(phi) * r });
	}
	return out;
}

function cross(
	a: PolyhedronMeshVertex,
	b: PolyhedronMeshVertex,
): PolyhedronMeshVertex {
	return {
		x: a.y * b.z - a.z * b.y,
		y: a.z * b.x - a.x * b.z,
		z: a.x * b.y - a.y * b.x,
	};
}

function sub(
	a: PolyhedronMeshVertex,
	b: PolyhedronMeshVertex,
): PolyhedronMeshVertex {
	return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function dot(a: PolyhedronMeshVertex, b: PolyhedronMeshVertex): number {
	return a.x * b.x + a.y * b.y + a.z * b.z;
}

/**
 * Incremental convex hull for points near a sphere (O(n²) — fine for ≤128 pts).
 */
function convexHullTriangles(
	points: PolyhedronMeshVertex[],
): PolyhedronMeshTriangle[] {
	if (points.length < 4) {
		return points.length === 3 ? [[0, 1, 2]] : [];
	}
	/** Seed tetra from first 4 non-coplanar-ish. */
	let faces: Array<{
		tri: PolyhedronMeshTriangle;
		n: PolyhedronMeshVertex;
		d: number;
	}> = [];
	const makeFace = (a: number, b: number, c: number) => {
		const ab = sub(points[b], points[a]);
		const ac = sub(points[c], points[a]);
		const n = normalize(cross(ab, ac));
		const d = dot(n, points[a]);
		return { tri: [a, b, c] as const, n, d };
	};
	faces = [
		makeFace(0, 1, 2),
		makeFace(0, 2, 3),
		makeFace(0, 3, 1),
		makeFace(1, 3, 2),
	];
	/** Orient outward (away from centroid). */
	const centroid = {
		x: points.slice(0, 4).reduce((s, p) => s + p.x, 0) / 4,
		y: points.slice(0, 4).reduce((s, p) => s + p.y, 0) / 4,
		z: points.slice(0, 4).reduce((s, p) => s + p.z, 0) / 4,
	};
	faces = faces.map((f) => {
		if (dot(f.n, centroid) > f.d) {
			const [a, b, c] = f.tri;
			return makeFace(a, c, b);
		}
		return f;
	});

	for (let pi = 4; pi < points.length; pi++) {
		const p = points[pi];
		const visible: number[] = [];
		for (let fi = 0; fi < faces.length; fi++) {
			if (dot(faces[fi].n, p) > faces[fi].d + 1e-9) {
				visible.push(fi);
			}
		}
		if (visible.length === 0) {
			continue;
		}
		const edgeCount = new Map<
			string,
			{ a: number; b: number; n: number }
		>();
		const addEdge = (a: number, b: number) => {
			const key = a < b ? `${a}_${b}` : `${b}_${a}`;
			const cur = edgeCount.get(key);
			if (cur) {
				edgeCount.set(key, { ...cur, n: cur.n + 1 });
			} else {
				edgeCount.set(key, { a, b, n: 1 });
			}
		};
		for (const fi of visible) {
			const [a, b, c] = faces[fi].tri;
			addEdge(a, b);
			addEdge(b, c);
			addEdge(c, a);
		}
		const visibleSet = new Set(visible);
		faces = faces.filter((_, i) => !visibleSet.has(i));
		for (const { a, b, n } of edgeCount.values()) {
			if (n !== 1) {
				continue;
			}
			/** Horizon edge — wind so new face faces outward. */
			const f1 = makeFace(a, b, pi);
			const f2 = makeFace(a, pi, b);
			const mid = {
				x: (points[a].x + points[b].x + p.x) / 3,
				y: (points[a].y + points[b].y + p.y) / 3,
				z: (points[a].z + points[b].z + p.z) / 3,
			};
			faces.push(dot(f1.n, mid) >= f1.d - 1e-9 ? f1 : f2);
		}
	}
	return faces.map((f) => f.tri);
}

function applyIrregularity(
	vertices: PolyhedronMeshVertex[],
	irregularity: number,
	seed: number,
): PolyhedronMeshVertex[] {
	const amp = clamp(irregularity, 0, 1);
	if (amp < 1e-6) {
		return vertices.map((v) => ({ ...v }));
	}
	return vertices.map((v, i) => {
		const n = normalize(v);
		const j = 1 + (polyhedronHash01(seed, i) * 2 - 1) * amp * 0.35;
		return { x: n.x * j, y: n.y * j, z: n.z * j };
	});
}

function uniqueFaceCount(triangles: PolyhedronMeshTriangle[]): number {
	return triangles.length;
}

/**
 * Build unit-sphere-ish polyhedron mesh (center 0, extent ~1).
 * Caller scales into part AABB.
 */
export function buildPolyhedronMesh(
	params: BuildPolyhedronMeshParams = {},
): PolyhedronMesh {
	const irregularity = clamp(params.irregularity ?? 0, 0, 1);
	const seed = Math.floor(params.seed ?? 1);
	const targetFaces =
		typeof params.targetFaces === "number" && params.targetFaces >= 4
			? Math.round(clamp(params.targetFaces, 4, 128))
			: undefined;

	let family: PolyhedronFamily =
		params.family ?? (targetFaces ? "convex" : "icosahedron");
	if (targetFaces && !params.family) {
		family = "convex";
	}

	let vertices: PolyhedronMeshVertex[];
	let triangles: PolyhedronMeshTriangle[];

	if (family === "convex" || (targetFaces && family === "convex")) {
		const pointCount = Math.max(
			4,
			Math.round((targetFaces ?? 24) * 0.55) + 2,
		);
		const pts = fibonacciSphere(pointCount, seed);
		/** Light pre-jitter so hull is irregular before optional irregularity. */
		const jittered = pts.map((p, i) => {
			const j = 1 + (polyhedronHash01(seed, i + 7) * 2 - 1) * 0.08;
			return { x: p.x * j, y: p.y * j, z: p.z * j };
		});
		triangles = convexHullTriangles(jittered);
		vertices = jittered;
	} else {
		const base =
			family === "tetrahedron"
				? tetrahedronBase()
				: family === "octahedron"
					? octahedronBase()
					: family === "dodecahedron"
						? dodecahedronBase()
						: icosahedronBase();
		const detail = clamp(Math.floor(params.detail ?? 0), 0, 3);
		const sub = subdivide(base.vertices, base.triangles, detail);
		vertices = sub.vertices;
		triangles = sub.triangles;
	}

	vertices = applyIrregularity(vertices, irregularity, seed);
	/** Re-normalize lightly so extent stays stable after jitter. */
	let maxR = 1e-6;
	for (const v of vertices) {
		maxR = Math.max(maxR, Math.hypot(v.x, v.y, v.z));
	}
	vertices = vertices.map((v) => ({
		x: v.x / maxR,
		y: v.y / maxR,
		z: v.z / maxR,
	}));

	return {
		vertices,
		triangles,
		family,
		faceCount: uniqueFaceCount(triangles),
	};
}

/**
 * Scale unit polyhedron into AABB (ox,oy,oz center; sx,sy,sz full extents).
 * Returns flat triangle list for tessellate (three verts each).
 */
export function polyhedronMeshToWorldTriangles(params: {
	mesh: PolyhedronMesh;
	ox: number;
	oy: number;
	oz: number;
	sx: number;
	sy: number;
	sz: number;
}): Array<
	readonly [PolyhedronMeshVertex, PolyhedronMeshVertex, PolyhedronMeshVertex]
> {
	const hx = params.sx * 0.5;
	const hy = params.sy * 0.5;
	const hz = params.sz * 0.5;
	const mapV = (v: PolyhedronMeshVertex): PolyhedronMeshVertex => ({
		x: params.ox + v.x * hx,
		y: params.oy + v.y * hy,
		z: params.oz + v.z * hz,
	});
	return params.mesh.triangles.map(([a, b, c]) => [
		mapV(params.mesh.vertices[a]),
		mapV(params.mesh.vertices[b]),
		mapV(params.mesh.vertices[c]),
	]);
}
